import { useEffect, useState } from "react";
import { Button, DatePicker, Modal, Space, Flex, Tag } from "antd";
import {
  CalendarOutlined,
  ArrowRightOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { displayMessage, postAPI } from "../../../utils/common";
import { EDIT_VENDOR } from "../../../constants/api";
import { BILLING_CYCLE, isSuperAdmin } from "../../../constants/constent";
import { ERROR_MSG_TYPE, SUCCESS_MSG_TYPE } from "../../../constants/messages";
import VendorDetailCard from "./VendorDetailCard";

/**
 * Reusable "Change Next Billing Date" control.
 *
 * Lets a super-admin/admin update a vendor's next_billing_date. Super admins
 * may pick any date; others cannot pick past dates. Calls onSaved after a
 * successful update so the caller can refresh.
 *
 * Usage:
 *  - Inline:  <ChangeNextBillingDate vendor={vendor} onSaved={fn} />
 *  - Modal:   <ChangeNextBillingDate vendor={vendor} asModal open={open}
 *               onClose={fn} onSaved={fn} />
 */
function ChangeNextBillingDate({
  vendor,
  asModal = false,
  open = false,
  onClose,
  onSaved,
}) {
  const [nextBillingDate, setNextBillingDate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Keep the picker in sync whenever the vendor (or modal open state) changes.
  useEffect(() => {
    setNextBillingDate(
      vendor?.next_billing_date ? dayjs(vendor.next_billing_date) : null,
    );
  }, [vendor, open]);

  const handleSave = () => {
    if (!vendor) return;
    setSaving(true);
    postAPI(
      EDIT_VENDOR,
      {
        id: vendor.id,
        next_billing_date: nextBillingDate
          ? nextBillingDate.format("YYYY-MM-DD")
          : null,
      },
      (res) => {
        setSaving(false);
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          if (onSaved) onSaved(res);
          if (asModal && onClose) onClose();
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
      },
      () => {
        setSaving(false);
        displayMessage(ERROR_MSG_TYPE, "Failed to update next billing date");
      },
    );
  };

  if (!vendor) return null;

  const PRIMARY = "var(--primary-color)";

  const currentDate = vendor.next_billing_date
    ? dayjs(vendor.next_billing_date)
    : null;

  const cycleMonths = Number(vendor.billing_cycle) || 1;
  const cycleLabel =
    BILLING_CYCLE.find((c) => c.value === cycleMonths)?.label || "Cycle";

  const disabledDate = isSuperAdmin()
    ? undefined
    : (current) => current && current < dayjs().startOf("day");

  // Quick shift helpers operate on the current base date (or today).
  const shiftFrom = (base, months) =>
    (base || dayjs()).clone().add(months, "month");

  const picker = (
    <DatePicker
      value={nextBillingDate}
      format="YYYY-MM-DD"
      onChange={(date) => setNextBillingDate(date)}
      disabledDate={disabledDate}
      style={{ width: "100%" }}
      allowClear
    />
  );

  /* ---------- Inline (compact) ---------- */
  if (!asModal) {
    return (
      <div style={{ marginTop: 16 }}>
        <strong>Change Next Billing Date</strong>
        <Space wrap style={{ marginTop: 8 }}>
          <div style={{ width: 200 }}>{picker}</div>
          <Button type="primary" loading={saving} onClick={handleSave}>
            Save
          </Button>
        </Space>
      </div>
    );
  }

  /* ---------- Modal (redesigned) ---------- */
  return (
    <Modal
      title={
        <Flex align="center" gap={8}>
          <CalendarOutlined style={{ color: PRIMARY }} />
          Change Next Billing Date
        </Flex>
      }
      open={open}
      onCancel={onClose}
      destroyOnClose
      width={760}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
        >
          Save Changes
        </Button>,
      ]}
    >
      {/* Vendor summary */}
      <div style={{ marginBottom: 18 }}>
        <VendorDetailCard vendor={vendor} />
      </div>

      {/* Current -> New */}
      <Flex align="stretch" gap={16} wrap style={{ marginBottom: 18 }}>
        {/* Current */}
        <div
          style={{
            flex: "1 1 200px",
            background: "#f8f9fc",
            border: "1px solid #eef0f5",
            borderRadius: 12,
            padding: "14px 16px",
          }}
        >
          <Flex align="center" gap={6}>
            <ClockCircleOutlined style={{ color: "#9aa3af" }} />
            <span style={{ fontSize: 11, color: "#9aa3af", fontWeight: 600 }}>
              CURRENT NEXT BILLING
            </span>
          </Flex>
          <div
            style={{
              marginTop: 8,
              fontSize: 20,
              fontWeight: 700,
              color: "#1f2937",
            }}
          >
            {currentDate ? currentDate.format("DD MMM YYYY") : "Not set"}
          </div>
          <Tag style={{ marginTop: 8 }}>{cycleLabel}</Tag>
        </div>

        {/* Arrow */}
        <Flex align="center" justify="center" style={{ flex: "0 0 auto" }}>
          <ArrowRightOutlined style={{ fontSize: 20, color: PRIMARY }} />
        </Flex>

        {/* New */}
        <div
          style={{
            flex: "1 1 240px",
            background: "#fff",
            border: `1.5px solid var(--primary-color)`,
            borderRadius: 12,
            padding: "14px 16px",
          }}
        >
          <span style={{ fontSize: 11, color: PRIMARY, fontWeight: 700 }}>
            NEW NEXT BILLING
          </span>
          <div style={{ marginTop: 8 }}>{picker}</div>
          {nextBillingDate && (
            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
              {nextBillingDate.format("dddd, DD MMMM YYYY")}
            </div>
          )}
        </div>
      </Flex>

      {/* Quick shift helpers */}
      <div>
        <span style={{ fontSize: 12, color: "#9aa3af", marginRight: 8 }}>
          Quick set:
        </span>
        <Space wrap>
          <Button
            size="small"
            onClick={() => setNextBillingDate(dayjs())}
          >
            Today
          </Button>
          <Button
            size="small"
            onClick={() => setNextBillingDate(shiftFrom(currentDate, 1))}
          >
            +1 Month
          </Button>
          <Button
            size="small"
            onClick={() =>
              setNextBillingDate(shiftFrom(currentDate, cycleMonths))
            }
          >
            +1 Cycle ({cycleLabel})
          </Button>
        </Space>
      </div>
    </Modal>
  );
}

export default ChangeNextBillingDate;
